import { Select as BaseSelect } from '@base-ui/react/select';
import { RiArrowDownSLine, RiCheckLine } from '@remixicon/react';
import { cn } from '@/shared/lib/cn';
import { useFieldControlProps } from './field';
import { renderIcon, type IconLike } from './icon';
import { fieldBoxStyles } from './input';
import { usePortalContainer, type PortalContainer } from './lib/portal';
import { dropdownItem, dropdownSurface, popupMotion } from './lib/surfaces';

export interface SelectOption<T> {
  value: T;
  label: string;
  icon?: IconLike;
  /** Second line in the list (400 12/16 White/60). */
  description?: React.ReactNode;
  disabled?: boolean;
}

export interface SelectProps<T> {
  options: readonly SelectOption<T>[];
  value?: T | null;
  defaultValue?: T | null;
  onValueChange?: (value: T | null) => void;
  placeholder?: React.ReactNode;
  /** `sm` 24 · `md` 32 (default) · `lg` 36. */
  size?: 'sm' | 'md' | 'lg';
  leftIcon?: IconLike;
  disabled?: boolean;
  invalid?: boolean;
  required?: boolean;
  readOnly?: boolean;
  /** Form field name (hidden input). */
  name?: string;
  id?: string;
  'aria-label'?: string;
  'aria-describedby'?: string;
  /** Compare object values. */
  isItemEqualToValue?: (itemValue: T, value: T) => boolean;
  /** Custom trigger content for the selected option. */
  renderValue?: (option: SelectOption<T> | undefined) => React.ReactNode;
  /** Trigger class. */
  className?: string;
  popupClassName?: string;
  open?: boolean;
  onOpenChange?: (open: boolean) => void;
  /** Portal target (scaled stage). Defaults to the nearest `PortalContainerProvider`, else `<body>`. */
  container?: PortalContainer;
}

/**
 * Select — Base UI Select styled as the product field (h32, r8, White/10, 400 13/18, trailing arrow-down-s-line 16,
 * spec §4.5). The list uses the dropdown surface (§9 Q14) and opens below the trigger.
 */
export function Select<T>({
  options,
  value,
  defaultValue,
  onValueChange,
  placeholder = 'Tanlang',
  size = 'md',
  leftIcon,
  disabled,
  invalid,
  required,
  readOnly,
  name,
  id,
  'aria-label': ariaLabel,
  'aria-describedby': ariaDescribedBy,
  isItemEqualToValue,
  renderValue,
  className,
  popupClassName,
  open,
  onOpenChange,
  container,
}: SelectProps<T>) {
  const field = useFieldControlProps({ id, disabled, required, invalid, 'aria-describedby': ariaDescribedBy });
  const portalContainer = usePortalContainer(container);
  const s = fieldBoxStyles({ size, invalid: field.invalid });
  const items = options.map((o) => ({ value: o.value, label: o.label }));
  const findOption = (v: unknown) =>
    options.find((o) => (isItemEqualToValue && v != null ? isItemEqualToValue(o.value, v as T) : Object.is(o.value, v)));

  return (
    <BaseSelect.Root<T>
      items={items}
      value={value}
      defaultValue={defaultValue}
      onValueChange={(next) => onValueChange?.(next as T | null)}
      isItemEqualToValue={isItemEqualToValue}
      disabled={field.disabled}
      required={field.required}
      readOnly={readOnly}
      name={name}
      open={open}
      onOpenChange={onOpenChange ? (next) => onOpenChange(next) : undefined}
      modal={false}
    >
      <BaseSelect.Trigger
        id={field.id}
        aria-label={ariaLabel}
        aria-describedby={field['aria-describedby']}
        aria-invalid={field.invalid || undefined}
        className={s.box({
          class: cn(
            'cursor-pointer text-left outline-none focus-visible:border-focus data-popup-open:border-focus',
            size === 'sm' ? 'text-xs tracking-normal' : 't-field',
            'data-disabled:cursor-not-allowed data-disabled:text-fg-tertiary',
            className,
          ),
        })}
      >
        {renderIcon(leftIcon, s.icon())}
        <BaseSelect.Value className="min-w-0 flex-1 truncate data-placeholder:text-fg-secondary" placeholder={placeholder}>
          {renderValue ? (v: unknown) => renderValue(findOption(v)) : undefined}
        </BaseSelect.Value>
        <BaseSelect.Icon className="flex shrink-0">
          <RiArrowDownSLine
            aria-hidden
            className="size-16 text-icon transition-transform duration-150 group-data-popup-open/field:rotate-180"
          />
        </BaseSelect.Icon>
      </BaseSelect.Trigger>
      <BaseSelect.Portal container={portalContainer}>
        <BaseSelect.Positioner alignItemWithTrigger={false} sideOffset={4} align="start" className="z-75 outline-none">
          <BaseSelect.Popup
            className={cn(
              dropdownSurface,
              popupMotion,
              'max-h-[min(var(--available-height),20rem)] min-w-(--anchor-width) overflow-y-auto overscroll-contain',
              popupClassName,
            )}
          >
            <BaseSelect.List className="flex flex-col gap-2">
              {options.map((option) => (
                <BaseSelect.Item
                  key={String(option.value)}
                  value={option.value}
                  label={option.label}
                  disabled={option.disabled}
                  className={cn(dropdownItem, 'gap-8 py-6 data-selected:text-fg')}
                >
                  {renderIcon(option.icon, 'size-16 text-icon')}
                  <span className="flex min-w-0 flex-1 flex-col">
                    <BaseSelect.ItemText className="truncate">{option.label}</BaseSelect.ItemText>
                    {option.description && (
                      <span className="truncate text-xs text-fg-secondary">{option.description}</span>
                    )}
                  </span>
                  <BaseSelect.ItemIndicator className="flex shrink-0">
                    <RiCheckLine aria-hidden className="size-16 text-accent-2" />
                  </BaseSelect.ItemIndicator>
                </BaseSelect.Item>
              ))}
            </BaseSelect.List>
          </BaseSelect.Popup>
        </BaseSelect.Positioner>
      </BaseSelect.Portal>
    </BaseSelect.Root>
  );
}
